import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import format from "date-fns/format";
import { it } from "date-fns/locale";

// import styles
import "./Dashboard.css";

// import componenti
import SpeseFilter from "./SpeseFilter";

// import hooks
import { useCollection } from "../../hooks/useCollection";
import { useAuthContext } from "../../hooks/useAuthContext";

export default function Dashboard() {
  const navigazione = useNavigate();
  const { user } = useAuthContext();
  const { documents, error } = useCollection("spese", [
    "autore.id",
    "==",
    user.uid,
  ]);
  const [activeFilter, setActiveFilter] = useState("tutti");
  const [searchTerm, setSearchTerm] = useState("");
  const [totale, setTotale] = useState(0);
  const [anno, setAnno] = useState("tutti");
  const [pagamento, setPagamento] = useState("tutti");
  const [sortConfig, setSortConfig] = useState({ key: "data", dir: "desc" });

  const changeFilter = (newFilter) => {
    setActiveFilter(newFilter);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleHeaderSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { ...prev, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  const filtredDoc = useMemo(() => {
    if (!documents) return null;
    return documents.filter((doc) => {
        // Filtraggio per mese
        let matchesFilter = true;
        switch (activeFilter) {
          case "tutti":
            matchesFilter = true;
            break;
          case "gennaio":
          case "febbraio":
          case "marzo":
          case "aprile":
          case "maggio":
          case "giugno":
          case "luglio":
          case "agosto":
          case "settembre":
          case "ottobre":
          case "novembre":
          case "dicembre":
            const annoAttuale = format(doc.createdAt.toDate(), "y");
            const mese = format(doc.createdAt.toDate(), "MMMM", { locale: it });
            matchesFilter =
              mese === activeFilter && (anno === "tutti" || annoAttuale.toString() === anno);
            break;
          default:
            matchesFilter = true;
        }

        // Filtraggio per termine di ricerca
        const matchesSearch =
          searchTerm === "" ||
          (doc.nome && doc.nome.toLowerCase().includes(searchTerm)) ||
          (doc.importo && doc.importo.toString().includes(searchTerm)) ||
          (doc.dettagli && doc.dettagli.toLowerCase().includes(searchTerm));

        // Filtraggio per pagamento
        const matchesPagamento = pagamento === "tutti" || doc.pagamento === pagamento;

        // Deve corrispondere a tutti i filtri
        return matchesFilter && matchesSearch && matchesPagamento;
      });
  }, [documents, activeFilter, searchTerm, anno, pagamento]);

  const sortedDocs = useMemo(() => {
    if (!filtredDoc) return null;
    const sorted = [...filtredDoc];
    const collator = new Intl.Collator("it", { sensitivity: "base", numeric: true });

    sorted.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (sortConfig.key === "data") {
        valA = a.createdAt?.toDate().getTime() || 0;
        valB = b.createdAt?.toDate().getTime() || 0;
      } else if (sortConfig.key === "importo") {
        valA = Number(a.importo) || 0;
        valB = Number(b.importo) || 0;
      } else {
        valA = (valA || "").toString();
        valB = (valB || "").toString();
      }

      let cmp;
      if (typeof valA === "number" && typeof valB === "number") {
        cmp = valA - valB;
      } else {
        cmp = collator.compare(valA, valB);
      }
      return sortConfig.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [filtredDoc, sortConfig]);

  useEffect(() => {
    if (filtredDoc) {
      const total = filtredDoc
        .map((item) => +item.importo)
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      setTotale(total);
    }
  }, [filtredDoc]);

  return (
    <div>
      <div className="title-container">
        <h2 className="page-title">Spese</h2>
        <button onClick={() => navigazione("/spesa")} className="btn">
          Inserisci nuova spesa
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {documents && (
        <SpeseFilter
          change={changeFilter}
          filtro={activeFilter}
          onSearch={handleSearch}
          anno={anno}
          setAnno={setAnno}
          pagamento={pagamento}
          setPagamento={setPagamento}
        />
      )}
      <div className="totale-spese">
        <p>Totale: <span className="importo">{totale.toFixed(2).replace('.', ',')} €</span></p>
      </div>
      
      {sortedDocs && sortedDocs.length === 0 && (
        <p>Nessuna spesa trovata con i filtri selezionati</p>
      )}

      {sortedDocs && sortedDocs.length > 0 && (
        <div className="project-table-wrapper">
          <table className="project-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleHeaderSort("data")}>
                  Data {sortConfig.key === "data" ? (sortConfig.dir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="sortable" onClick={() => handleHeaderSort("nome")}>
                  Fornitore {sortConfig.key === "nome" ? (sortConfig.dir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th className="sortable" onClick={() => handleHeaderSort("importo")}>
                  Importo {sortConfig.key === "importo" ? (sortConfig.dir === "asc" ? "▲" : "▼") : ""}
                </th>
                <th>Dettagli</th>
                <th className="sortable" onClick={() => handleHeaderSort("pagamento")}>
                  Pagamento {sortConfig.key === "pagamento" ? (sortConfig.dir === "asc" ? "▲" : "▼") : ""}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((doc) => (
                <tr
                  key={doc.id}
                  onClick={() => navigazione(`/spesa/${doc.id}`)}
                  className="clickable-row"
                >
                  <td>
                    {doc.data
                      ? format(doc.data.toDate(), "dd/MM/yyyy", { locale: it })
                      : "-"}
                  </td>
                  <td>{doc.nome}</td>
                  <td style={{ fontWeight: "bold" }}>{doc.importo} €</td>
                  <td className="truncate-cell" title={doc.dettagli}>
                    {doc.dettagli}
                  </td>
                  <td>{doc.pagamento}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
